import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useMemo } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { FollowButton } from '@/components/ui/FollowButton';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useTeam, useTeamPlayers } from '@/features/leagues/hooks';
import { useProfile, useUpdateProfile } from '@/features/profile/hooks';
import { useAuth } from '@/lib/auth/AuthProvider';
import { formatPrice } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import type { PlayerPosition, Tables } from '@/lib/supabase/types';

const POSITION_ORDER: PlayerPosition[] = ['GK', 'DEF', 'MID', 'FWD'];

function PlayerRow({ player }: { player: Tables<'players'> }) {
  const router = useRouter();
  const { numeralOptions } = useLocale();
  return (
    <Pressable
      accessibilityRole="button"
      onPress={() => router.push(`/player/${player.id}`)}
      className="flex-row items-center gap-3 px-4 py-3 active:bg-surface-2/60"
    >
      <TeamCrest uri={player.photo_url} name={player.known_as ?? player.name} size="md" />
      <View className="flex-1">
        <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
          {player.known_as ?? player.name}
        </AppText>
        {player.nationality ? <AppText variant="micro">{player.nationality}</AppText> : null}
      </View>
      <AppText variant="micro" weight="bold" tabular className="text-volt">
        {formatPrice(player.fantasy_price, numeralOptions)}
      </AppText>
    </Pressable>
  );
}

export default function TeamScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useTranslation();
  const { userId } = useAuth();
  const teamQuery = useTeam(id);
  const playersQuery = useTeamPlayers(id);
  const profileQuery = useProfile();
  const updateProfile = useUpdateProfile();
  const isFavorite = Boolean(id) && profileQuery.data?.favorite_team_id === id;

  const grouped = useMemo(() => {
    const players = playersQuery.data ?? [];
    return POSITION_ORDER.map((position) => ({
      position,
      players: players.filter((player) => player.position === position),
    })).filter((group) => group.players.length > 0);
  }, [playersQuery.data]);

  if (teamQuery.isPending) {
    return (
      <Screen>
        <BackHeader />
        <View className="gap-4 px-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </View>
      </Screen>
    );
  }

  if (teamQuery.isError || !teamQuery.data) {
    return (
      <Screen>
        <BackHeader />
        <ErrorState error={teamQuery.error} onRetry={() => teamQuery.refetch()} />
      </Screen>
    );
  }

  const team = teamQuery.data;

  return (
    <Screen>
      <BackHeader title={team.name} />
      <ScrollView contentContainerStyle={{ paddingBottom: 32 }}>
        <Card className="mx-4 mb-4 gap-4 px-4 py-4">
          <View className="flex-row items-center gap-4">
            <TeamCrest uri={team.logo_url} name={team.name} size="xl" />
            <View className="flex-1 gap-1">
              <AppText variant="heading">{team.name}</AppText>
              {team.country ? <AppText variant="micro">{team.country}</AppText> : null}
              {team.venue ? (
                <AppText variant="micro">
                  {t('team.venue')}: {team.venue}
                </AppText>
              ) : null}
            </View>
            <FollowButton entityType="team" entityId={id} />
          </View>
          {userId ? (
            <Button
              label={isFavorite ? t('team.unsetFavorite') : t('team.setFavorite')}
              variant={isFavorite ? 'secondary' : 'ghost'}
              compact
              loading={updateProfile.isPending}
              onPress={() =>
                updateProfile.mutate({ favorite_team_id: isFavorite ? null : (id ?? null) })
              }
            />
          ) : null}
        </Card>

        <View className="px-4 pb-2">
          <AppText variant="heading">{t('team.squad')}</AppText>
        </View>

        {playersQuery.isPending ? (
          <View className="gap-3 px-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </View>
        ) : playersQuery.isError ? (
          <ErrorState error={playersQuery.error} onRetry={() => playersQuery.refetch()} />
        ) : grouped.length === 0 ? (
          <EmptyState
            icon="people-outline"
            title={t('team.noPlayersTitle')}
            body={t('team.noPlayersBody')}
          />
        ) : (
          grouped.map((group) => (
            <View key={group.position} className="mb-4 px-4">
              <AppText variant="micro" weight="bold" className="mb-2 uppercase text-muted">
                {t(`player.positions.${group.position}`)}
              </AppText>
              <Card className="overflow-hidden">
                {group.players.map((player, index) => (
                  <View key={player.id} className={index > 0 ? 'border-t border-line' : ''}>
                    <PlayerRow player={player} />
                  </View>
                ))}
              </Card>
            </View>
          ))
        )}
      </ScrollView>
    </Screen>
  );
}
